import React, { useEffect, useMemo, useRef } from 'react';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

function FaultCodesTable({ data = [] }) {
  const tableHostRef = useRef(null);
  const dataTableRef = useRef(null);
  const tableRows = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  useEffect(() => {
    const host = tableHostRef.current;
    if (!host || tableRows.length === 0) {
      return undefined;
    }

    resetTableHost(tableHostRef, dataTableRef);

    const table = document.createElement('table');
    table.className = 'table table-striped w-100 alarms-table fault-codes-table';
    table.style.width = '100%';
    host.appendChild(table);

    const tableData = tableRows.map((row) => [
      row?.code_family ?? '',
      row?.display_code ?? row?.fault_code ?? '',
      row?.description ?? '',
    ]);

    dataTableRef.current = new DataTable(table, {
      data: tableData,
      columns: [
        {
          title: 'Code family',
          className: 'fault-codes-table__family',
          render: (cellData) => (
            cellData === null || cellData === undefined || cellData === '' ? '—' : cellData
          ),
        },
        {
          title: 'Fault Code',
          className: 'fault-codes-table__code',
          render: (cellData, type) => {
            if (type === 'sort' || type === 'type') {
              const numeric = Number(String(cellData ?? '').replace(/^F/i, ''));
              return Number.isFinite(numeric) ? numeric : cellData ?? '';
            }
            return cellData === null || cellData === undefined || cellData === '' ? '—' : cellData;
          },
        },
        {
          title: 'Description',
          className: 'fault-codes-table__description',
          render: (cellData) => (
            cellData === null || cellData === undefined || cellData === '' ? '—' : cellData
          ),
        },
      ],
      order: [[0, 'asc'], [1, 'asc']],
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: true,
      paging: true,
      ordering: true,
      autoWidth: false,
      initComplete: () => {
        host.querySelectorAll('table.fault-codes-table').forEach((el) => {
          el.style.width = '100%';
        });
        dataTableRef.current?.columns.adjust().draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: {
          search: {
            placeholder: 'Search table...',
          },
        },
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    return () => {
      resetTableHost(tableHostRef, dataTableRef);
    };
  }, [tableRows]);

  useEffect(() => () => {
    resetTableHost(tableHostRef, dataTableRef);
  }, []);

  return (
    <>
      {tableRows.length === 0 && (
        <p className="mb-2">No data available</p>
      )}
      <div
        ref={tableHostRef}
        className="alarms-table-host"
      />
    </>
  );
}

export default FaultCodesTable;
